import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';

import { Button as UiButton, Chip, ShellHeader } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';

import { agentService } from '../services/agentService';
import { ChatCompletionsTab } from '../components/api-explorer/ChatCompletionsTab';
import { ResponsesTab } from '../components/api-explorer/ResponsesTab';
import { AssistantsTab } from '../components/api-explorer/AssistantsTab';
import type { AgentDefinitionDto } from '../types/agent';

export const ApiExplorerDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (slug) loadAgent(slug);
  }, [slug]);

  const loadAgent = async (agentSlug: string) => {
    setLoading(true);
    setError(null);
    try {
      const a = await agentService.getAgentBySlug(agentSlug);
      setAgent(a);
    } catch {
      setError(`Agent with slug "${agentSlug}" not found.`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="api-explorer-detail" data-testid="api-explorer-detail-form-page">
        <ShellHeader title="API Explorer" description="Loading agent…" data-testid="api-explorer-detail-header" />
        <PageContainer data-testid="api-explorer-detail-page-container">
          <LoadingSkeleton rows={6} data-testid="api-explorer-detail-loading" />
        </PageContainer>
      </div>
    );
  }

  if (error || !agent || !slug) {
    return (
      <div className="api-explorer-detail" data-testid="api-explorer-detail-form-page">
        <ShellHeader
          title="API Explorer"
          description="Agent not found."
          data-testid="api-explorer-detail-header"
          actions={
            <UiButton type="button" variant="outline" size="sm" onClick={() => navigate('/api-explorer')} data-testid="back-api-explorer-detail-btn">
              Back to API Explorer
            </UiButton>
          }
        />
        <PageContainer data-testid="api-explorer-detail-page-container">
          <InlineErrorPanel message={error ?? 'Agent not found.'} data-testid="api-explorer-detail-error-panel" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="api-explorer-detail" data-testid="api-explorer-detail-form-page">
      <ShellHeader
        title={agent.name}
        description={agent.description ?? 'Explore Chat Completions, Responses, and Assistants API data.'}
        data-testid="api-explorer-detail-header"
        actions={
          <div className="flex items-center gap-1.5">
            <Chip label={slug} className="font-mono text-xs" />
            <UiButton type="button" variant="outline" size="sm" onClick={() => navigate('/api-explorer')} data-testid="back-api-explorer-detail-btn">
              Back
            </UiButton>
          </div>
        }
      />

      <PageContainer data-testid="api-explorer-detail-page-container">
        {/* API Tabs */}
        <div className="border-b border-[var(--glass-border)] mb-3">
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            data-testid="api-explorer-detail-tabs"
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48, px: 3 },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab label="Chat Completions" data-testid="api-explorer-detail-tab-chat-completions" />
            <Tab label="Responses" data-testid="api-explorer-detail-tab-responses" />
            <Tab label="Assistants" data-testid="api-explorer-detail-tab-assistants" />
          </Tabs>
        </div>

        {/* Tab Content */}
        <div
          data-testid="api-explorer-detail-tab-content"
          className="p-3 rounded-lg border border-[var(--glass-border)] min-h-[300px]"
          style={{
            background: 'var(--glass-bg-subtle)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {activeTab === 0 && <ChatCompletionsTab agentId={agent.id} agentSlug={slug} />}
          {activeTab === 1 && <ResponsesTab agentId={agent.id} agentSlug={slug} />}
          {activeTab === 2 && <AssistantsTab agentId={agent.id} agentSlug={slug} />}
        </div>
      </PageContainer>
    </div>
  );
};
